import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  CubeTransparentIcon,
  CpuChipIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import ROICalculator from '../components/ROICalculator';
import PlatformArchitecture from '../components/PlatformArchitecture';
import AIEvolution from '../components/AIEvolution';

const HomePage: React.FC = () => {

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="gradient-bg text-white relative overflow-hidden min-h-screen flex items-center">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          {/* CTA Button */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <button className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300">
              <Cog6ToothIcon className="w-5 h-5" />
              <span>Enterprise AI, Blockchain & IoT Solutions</span>
            </button>
          </motion.div>

          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-4">
              <span className="text-white">AI Solutions That</span>
              <br />
              <span className="text-blue-300">Actually Work For You</span>
            </h1>
          </motion.div>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto text-gray-200 leading-relaxed"
          >
            Purpose-built AI tools that integrate with your business systems. Not another chatbot—real solutions for documents, databases, video, and more.
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/schedule-meeting"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center gap-2 text-lg shadow-lg"
            >
              Book a Meeting
            </Link>
            <a
              href="#how-it-works"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors duration-200 text-lg"
            >
              Learn More
            </a>
          </motion.div>
        </div>
      </section>

      {/* Products Showcase Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Solutions That Solve Real Problems
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              AI-powered tools for document processing, business intelligence, computer vision, plus blockchain payments and IoT management
            </p>
          </div>

          {/* AI Solutions Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">🤖 AI Solutions</h3>
              <p className="text-lg text-gray-600">Purpose-built tools that integrate with your workflows</p>
            </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* DB Talker Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 border-2 border-blue-200 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-blue-600 p-3 rounded-xl">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">DB Talker</h3>
                  <p className="text-blue-700 font-semibold">Business Intelligence Chat</p>
                </div>
              </div>
              
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Chat with your database in plain English. Get executive insights with charts and visualizations instantly.
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span className="text-gray-700">Ask questions in natural language</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span className="text-gray-700">Automatic chart generation</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span className="text-gray-700">No SQL knowledge required</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span className="text-gray-700">Mobile-first for on-the-go insights</span>
                </div>
              </div>

              <Link
                to="/db-talker"
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 w-full justify-center"
              >
                <span>Explore DB Talker</span>
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </motion.div>

            {/* DocFlow AI Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl p-8 border-2 border-purple-200 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-purple-600 p-3 rounded-xl">
                  <DocumentTextIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">DocFlow AI</h3>
                  <p className="text-purple-700 font-semibold">Document Intelligence</p>
                </div>
              </div>
              
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Transform documents into intelligence. Extract VAT, payment terms, and contract obligations automatically with flexible deployment.
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span className="text-gray-700">Smart document processing & OCR</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span className="text-gray-700">Flexible private/cloud/hybrid</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span className="text-gray-700">Track deadlines & milestones</span>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span className="text-gray-700">Human verification workflow</span>
                </div>
              </div>

              <Link
                to="/docflow-ai"
                className="inline-flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors duration-200 w-full justify-center"
              >
                <span>Explore DocFlow AI</span>
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </motion.div>

            {/* VisionEdge AI Card */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl shadow-xl p-8 border-2 border-orange-200 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-orange-600 p-3 rounded-xl">
                  <VideoCameraIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">VisionEdge AI</h3>
                  <p className="text-orange-700 font-semibold">Real-Time Video Intelligence</p>
                </div>
              </div>
              
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Deploy AI-powered vision at the edge. Real-time object detection for safety, security, and quality control—no cloud required.
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span className="text-gray-700">Real-time object detection</span>
                </div>
                <div className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span className="text-gray-700">PPE & safety compliance</span>
                </div>
                <div className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span className="text-gray-700">Quality control automation</span>
                </div>
                <div className="flex items-start">
                  <span className="text-orange-600 mr-2">✓</span>
                  <span className="text-gray-700">Complete video privacy</span>
                </div>
              </div>

              <Link
                to="/visionedge-ai"
                className="inline-flex items-center space-x-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors duration-200 w-full justify-center"
              >
                <span>Explore VisionEdge AI</span>
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
          </div>

          {/* Blockchain & IoT Solutions Section - Side by Side */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">⛓️ Blockchain & 📡 IoT Solutions</h3>
              <p className="text-lg text-gray-600">Secure Digital Infrastructure & Connected Device Management</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Blockchain Wallet Card */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl shadow-xl p-8 border-2 border-emerald-200 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-emerald-600 p-3 rounded-xl">
                    <CubeTransparentIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">Blockchain Wallet</h3>
                    <p className="text-emerald-700 font-semibold">Private Digital Wallet Infrastructure</p>
                  </div>
                </div>
                
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  Eliminate transaction fees with private blockchain infrastructure. Instant settlements, enterprise security, and complete control—without cryptocurrency complexity.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <span className="text-emerald-600 mr-2">✓</span>
                    <span className="text-gray-700">Zero-fee internal transactions</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-600 mr-2">✓</span>
                    <span className="text-gray-700">Instant settlement in seconds</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-600 mr-2">✓</span>
                    <span className="text-gray-700">Fiat-pegged tokens (no volatility)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-emerald-600 mr-2">✓</span>
                    <span className="text-gray-700">Enterprise-grade security</span>
                  </div>
                </div>

                <Link
                  to="/blockchain-wallet"
                  className="inline-flex items-center space-x-2 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors duration-200 w-full justify-center"
                >
                  <span>Explore Blockchain Wallet</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
              </motion.div>

              {/* EdgeSphere Card */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl shadow-xl p-8 border-2 border-cyan-200 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-cyan-600 p-3 rounded-xl">
                    <CpuChipIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">QuickReach EdgeSphere™</h3>
                    <p className="text-cyan-700 font-semibold">IoT Platform</p>
                  </div>
                </div>
                
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  Stop paying per message and regain control of your IoT data. Fixed costs, unlimited messages, multi-cloud compatible—fully integrated with QuickReach workflows.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span className="text-gray-700">Fixed costs, unlimited messages</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span className="text-gray-700">Your infrastructure, your data</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span className="text-gray-700">Multi-cloud compatible (AWS, Azure, Google)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span className="text-gray-700">No vendor lock-in</span>
                  </div>
                </div>

                <Link
                  to="/edgesphere"
                  className="inline-flex items-center space-x-2 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors duration-200 w-full justify-center"
                >
                  <span>Explore EdgeSphere</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
              </motion.div>

              {/* InfluenceHub AI Card */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl shadow-xl p-8 border-2 border-pink-200 hover:shadow-2xl transition-shadow duration-300"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-pink-600 p-3 rounded-xl">
                    <UsersIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">InfluenceHub AI</h3>
                    <p className="text-pink-700 font-semibold">Influencer Marketing Platform</p>
                  </div>
                </div>
                
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  AI-powered influencer marketing for YouTube and Instagram. Self-hosted solution—own your data, customize features, no monthly SaaS fees.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <span className="text-pink-600 mr-2">✓</span>
                    <span className="text-gray-700">Campaign management with AI insights</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-pink-600 mr-2">✓</span>
                    <span className="text-gray-700">Influencer discovery & analytics</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-pink-600 mr-2">✓</span>
                    <span className="text-gray-700">Self-hosted, no monthly fees</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-pink-600 mr-2">✓</span>
                    <span className="text-gray-700">White-label for agencies</span>
                  </div>
                </div>

                <Link
                  to="/influencehub-ai"
                  className="inline-flex items-center space-x-2 bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors duration-200 w-full justify-center"
                >
                  <span>Explore InfluenceHub AI</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Value Proposition */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl p-8 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Why Choose QuickReach?
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-2">Ready to Deploy</div>
                <p className="text-gray-300">Pre-built solutions that work out of the box. Go live in days.</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-400 mb-2">Deep Integration</div>
                <p className="text-gray-300">Connect to your databases, documents, and existing systems.</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-400 mb-2">Flexible Deployment</div>
                <p className="text-gray-300">Cloud, on-premise, or hybrid. Your choice, your control.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Purpose-Built Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Beyond Generic AI Chatbots
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              ChatGPT and Claude are great for conversations. But your business needs tools that actually do the work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-red-500"
            >
              <h3 className="text-2xl font-bold text-red-600 mb-4">❌ Generic AI Chatbots</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Can't connect to your databases or systems</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Manual copy-paste for every task</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>No workflow automation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Results require human interpretation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Not built for specific business processes</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-green-500"
            >
              <h3 className="text-2xl font-bold text-green-600 mb-4">✅ Purpose-Built AI Tools</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Direct integration with your data sources</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Automated workflows that run without you</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Structured outputs ready for action</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Built for specific use cases (invoices, reports, video)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Deploy on your infrastructure when needed</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-blue-100">
              Deploy private AI, blockchain, and IoT solutions that you own and control. Schedule a meeting to discuss your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-meeting"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center gap-2 text-lg shadow-lg"
              >
                Schedule a Meeting
              </Link>
              <a
                href="#roi-calculator"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200 text-lg"
              >
                Calculate ROI
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section id="roi-calculator" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Calculate Your Productivity Gains
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how AI-powered automation can impact your team's efficiency.
            </p>
          </div>
          <ROICalculator />
        </div>
      </section>

      {/* Platform Architecture Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Enterprise-Grade Architecture
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built for scale, security, and performance.
            </p>
          </div>
          <PlatformArchitecture />
        </div>
      </section>

      {/* AI Evolution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              From Pilot to Production
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start small, scale fast. Our solutions grow with your business needs.
            </p>
          </div>
          <AIEvolution />
        </div>
      </section>
    </div>
  );
};

export default HomePage; 